import {IImposterMap, IImposterMapFactory, IImposterOptions} from '~/interfaces/imposterInterfaces'
import {ModelType} from '~/enums/modelType'
import {ObjectHelper} from '~/helpers/objectHelper'
import {Hash} from '~/interfaces/hashInterfaces'

/**
 * The "base" imposter map factory. Can use this class as a starting point for building imposter maps
 */
export default class DefaultImposterMapFactory implements IImposterMapFactory {
  /**
   * Build a hash for the default version of an imposter for a given ModelType
   *
   * @param modelType
   * @param options
   */
  imposterMapFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposterMap {
    if (modelType === ModelType.Text) {
      return this.textMapFor()
    } else if (modelType === ModelType.Email) {
        return this.emailMapFor()
    } else if (modelType === ModelType.Object) {
      return this.objectMapFor()
    } else if (modelType === ModelType.DateTime) {
      return this.dateTimeMapFor()
    } else if (modelType === ModelType.File) {
      return this.fileMapFor()
    } else if (modelType === ModelType.ObjectModel) {
      return this.objectModelMapFor()
    } else if (modelType === ModelType.ObjectModelRelation) {
      return this.objectModelRelationMapFor()
    } else if (modelType === ModelType.User) {
      return this.userMapFor()
    } else if (modelType === ModelType.EndPoint) {
      return this.apiEndPointMapFor();
    } else if (modelType === ModelType.Number) {
      return this.numberMapFor()
    } else if (modelType === ModelType.Boolean) {
      return this.booleanMapFor()
    } else {
      return {
        genericType: undefined,
        id: undefined,
        isCollection: undefined,
        modelType: undefined,
      }
    }
  }

  /**
   * A convenience method for slicing the properties/keys needed for an imposter map from an object/hash
   *
   * @param item
   */
  imposterMapFrom<T extends Hash>(item): IImposterMap {
    return ObjectHelper.sliceProperties(item as T, 'id', 'genericType', 'modelType', 'isCollection')
  }

  private textMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Text,
      modelType: ModelType.Text,
      isCollection: null, // need to set this externally, change this after the map is created
    }
  }

  private emailMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Text,
      modelType: ModelType.Email,
      isCollection: null, // need to set this externally
    }
  }

  private objectModelMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Object,
      modelType: ModelType.ObjectModel,
      isCollection: null, // need to set this externally
    }
  }

  private objectMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Object,
      modelType: null,
      isCollection: null, // need to set this externally
    }
  }

  private userMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Object,
      modelType: ModelType.User,
      isCollection: null, // need to set this externally
    }
  }

  private objectModelRelationMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Object,
      modelType: ModelType.ObjectModelRelation,
      isCollection: null, // need to set this externally
    }
  }

  private apiEndPointMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Object,
      modelType: ModelType.EndPoint,
      isCollection: null,

    }
  }

  private dateTimeMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.DateTime,
      modelType: ModelType.DateTime,
      isCollection: null, // need to set this externally
    }
  }

  private fileMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.File,
      modelType: ModelType.File,
      isCollection: null, // need to set this externally
    }
  }

  private numberMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Number,
      modelType: ModelType.Number,
      isCollection: null, // need to set this externally
    }
  }

  private booleanMapFor(): IImposterMap {
    return {
      id: null,
      genericType: ModelType.Boolean,
      modelType: ModelType.Boolean,
      isCollection: null, // need to set this externally
    }
  }
}